/**
 * Общая обвязка электрического элемента на чертеже: подложка, символ из каталога,
 * рамка выделения, метка «не подключён» и зона захвата.
 *
 * Сами элементы (розетка, выключатель, свет, распредкоробка) — отдельные компоненты
 * и отличаются формой подложки; всё остальное у них одинаковое и живёт здесь,
 * чтобы не расползлось по четырём файлам. Рисуется в локальных координатах:
 * начало в центре элемента, ось X вдоль стены.
 */
import type { PointerEvent, ReactNode } from "react";

export interface ItemFrameProps {
	/** Габариты символа на чертеже, мм (уже с учётом укрупнения и нижней границы) */
	width: number;
	height: number;
	color: string;
	selected: boolean;
	/** Прибор без кабеля — обводим предупреждающим цветом */
	unconnected?: boolean;
	/** Прибор в группе выделенного (светильник этого выключателя или наоборот) — мягкий ореол */
	highlighted?: boolean;
	mmPerPx: number;
	interactive?: boolean;
	/** id объекта — для зоны захвата, по нему тесты и обработчики узнают элемент */
	fixtureId: string;
	/** Подложка своей формы — её даёт конкретный элемент */
	backdrop: ReactNode;
	/** Символ из каталога */
	children: ReactNode;
	onPointerDown?: (e: PointerEvent<SVGGElement>) => void;
}

export const SELECTED_COLOR = "#2f6feb";
export const UNCONNECTED_COLOR = "#d97706";

export const ItemFrame = ({
	width: w,
	height: h,
	selected,
	unconnected = false,
	highlighted = false,
	mmPerPx,
	interactive = true,
	fixtureId,
	backdrop,
	children,
	onPointerDown,
}: ItemFrameProps) => {
	const px = (n: number) => n * mmPerPx;
	return (
		<g
			onPointerDown={onPointerDown}
			style={{
				cursor: interactive ? "grab" : "default",
				pointerEvents: interactive ? "auto" : "none",
			}}
		>
			{highlighted && !selected && (
				<rect
					x={-w / 2 - px(3)}
					y={-h / 2 - px(3)}
					width={w + px(6)}
					height={h + px(6)}
					rx={px(3)}
					fill={`${SELECTED_COLOR}22`}
					stroke={SELECTED_COLOR}
					strokeWidth={px(1)}
				/>
			)}
			{selected && (
				<rect
					x={-w / 2 - px(1)}
					y={-h / 2 - px(1)}
					width={w + px(2)}
					height={h + px(2)}
					rx={px(2)}
					fill="none"
					stroke={SELECTED_COLOR}
					strokeWidth={px(1)}
				/>
			)}
			{backdrop}
			{children}
			{/* Прибор без кабеля: пунктирное кольцо, чтобы на плане было видно, к чему ещё не подвели проводку */}
			{unconnected && !selected && (
				<rect
					x={-w / 2 - px(2.5)}
					y={-h / 2 - px(2.5)}
					width={w + px(5)}
					height={h + px(5)}
					rx={px(2)}
					fill="none"
					stroke={UNCONNECTED_COLOR}
					strokeWidth={px(1)}
					strokeDasharray={`${px(2.5)} ${px(2)}`}
				/>
			)}
			{/* Прозрачная зона захвата: сам значок мелкий, попасть по нему трудно */}
			<rect
				data-role="fixture"
				data-id={fixtureId}
				x={-w / 2 - px(6)}
				y={-h / 2 - px(6)}
				width={w + px(12)}
				height={h + px(12)}
				fill="transparent"
			/>
		</g>
	);
};
